import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { ArchetypeWithSubarchetypes } from "@liga/shared";
import { api } from "../api/client";
import type { AdminUnclassifiedRow } from "../api/client";
import { ErrorBox, Loading } from "./Loading";
import { SubarchetypeSelect } from "./DecksAdminPanel";

const NO_DECK_KEY = "__no_deck__";

/**
 * Backlog screen: every `event_result` without a subarchetype, grouped by
 * deck name so the admin can sweep through them. Each group surfaces the
 * "most-common subarchetype across history for this deck name" as a
 * suggestion, with a single click to apply it to every row in the group.
 *
 * Mutations write through `PATCH /api/admin/event-results/:id`, the same
 * endpoint the "Listas do deck" screen uses. After a successful classify
 * the row drops off the list (since it's no longer unclassified).
 */
export function UnclassifiedAdminPanel() {
  const queryClient = useQueryClient();

  const unclassifiedQuery = useQuery<AdminUnclassifiedRow[]>({
    queryKey: ["admin", "unclassified"],
    queryFn: api.adminUnclassifiedEventResults,
  });

  const archetypesQuery = useQuery<ArchetypeWithSubarchetypes[]>({
    queryKey: ["admin", "archetypes"],
    queryFn: api.adminArchetypes,
  });

  // Track per-row "saving" state so the relevant <select> can render
  // disabled. We don't optimistically remove the row — we wait for the
  // server roundtrip then drop it from the list via invalidate.
  const [busyIds, setBusyIds] = useState<Set<number>>(new Set());

  const classifyMutation = useMutation({
    mutationFn: async (input: {
      eventResultIds: number[];
      subarchetypeId: number | null;
    }) => {
      // Serialise the writes so we get cheap retry semantics if one fails.
      for (const id of input.eventResultIds) {
        await api.adminSetEventResultSubarchetype(id, {
          subarchetypeId: input.subarchetypeId,
        });
      }
    },
    onMutate: (input) => {
      setBusyIds((prev) => {
        const next = new Set(prev);
        for (const id of input.eventResultIds) next.add(id);
        return next;
      });
    },
    onSettled: (_data, _err, input) => {
      setBusyIds((prev) => {
        const next = new Set(prev);
        for (const id of input.eventResultIds) next.delete(id);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "unclassified"] });
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["matches"] });
      queryClient.invalidateQueries({ queryKey: ["metagame"] });
      queryClient.invalidateQueries({ queryKey: ["subarchetype"] });
      queryClient.invalidateQueries({
        queryKey: ["admin", "decks"],
      });
    },
  });

  const rows = unclassifiedQuery.data ?? [];
  const archetypes = archetypesQuery.data ?? [];

  // Group rows by deck name (case-insensitive), preserving the API order.
  const groups = useMemo(() => groupByDeck(rows), [rows]);

  return (
    <section className="rounded-2xl border border-line bg-white p-6 space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#8a8170] font-bold mb-1.5">
            Decks
          </div>
          <h2 className="font-display text-xl font-bold text-ink">
            Sem classificação
          </h2>
          <p className="text-[14px] leading-relaxed text-[#6b6356] mt-1">
            Todas as listas (event_results) que ainda não têm um
            subarquétipo. Quando há histórico para o mesmo nome de deck, a
            sugestão pré-preenche o seletor — você pode aceitar lista por
            lista ou aplicar a sugestão ao grupo inteiro.
          </p>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-[0.16em] text-[#8a8170] font-semibold">
            Pendentes
          </div>
          <div className="font-display text-2xl font-bold text-ink tabular-nums">
            {rows.length}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-[#a39a87]">
            em {groups.length} deck{groups.length === 1 ? "" : "s"}
          </div>
        </div>
      </header>

      {unclassifiedQuery.isLoading ? (
        <Loading label="Carregando backlog..." />
      ) : unclassifiedQuery.error ? (
        <ErrorBox error={unclassifiedQuery.error} />
      ) : rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-line bg-[#fbfaf6] px-4 py-10 text-center text-sm text-[#6b6356]">
          Tudo classificado. Nada a fazer aqui ✓
        </p>
      ) : (
        <div className="space-y-6">
          {groups.map((group) => (
            <DeckGroup
              key={group.key}
              group={group}
              archetypes={archetypes}
              busyIds={busyIds}
              onClassifyOne={(eventResultId, subarchetypeId) =>
                classifyMutation.mutate({
                  eventResultIds: [eventResultId],
                  subarchetypeId,
                })
              }
              onClassifyAll={(eventResultIds, subarchetypeId) =>
                classifyMutation.mutate({ eventResultIds, subarchetypeId })
              }
            />
          ))}
        </div>
      )}
    </section>
  );
}

interface DeckGroupShape {
  key: string;
  deckId: number | null;
  deckName: string | null;
  rows: AdminUnclassifiedRow[];
  suggestedSubarchetypeId: number | null;
  suggestedSubarchetypeName: string | null;
}

function groupByDeck(rows: AdminUnclassifiedRow[]): DeckGroupShape[] {
  const map = new Map<string, DeckGroupShape>();
  for (const r of rows) {
    const key = r.deckName ? r.deckName.toLowerCase() : NO_DECK_KEY;
    let g = map.get(key);
    if (!g) {
      g = {
        key,
        deckId: r.deckId,
        deckName: r.deckName,
        rows: [],
        suggestedSubarchetypeId: r.suggestedSubarchetypeId,
        suggestedSubarchetypeName: r.suggestedSubarchetypeName,
      };
      map.set(key, g);
    }
    g.rows.push(r);
  }
  return Array.from(map.values());
}

interface DeckGroupProps {
  group: DeckGroupShape;
  archetypes: ArchetypeWithSubarchetypes[];
  busyIds: Set<number>;
  onClassifyOne: (
    eventResultId: number,
    subarchetypeId: number | null,
  ) => void;
  onClassifyAll: (
    eventResultIds: number[],
    subarchetypeId: number | null,
  ) => void;
}

function DeckGroup({
  group,
  archetypes,
  busyIds,
  onClassifyOne,
  onClassifyAll,
}: DeckGroupProps) {
  const allIds = group.rows.map((r) => r.eventResultId);
  const anyBusy = allIds.some((id) => busyIds.has(id));
  const canApplySuggestion = group.suggestedSubarchetypeId !== null;

  return (
    <article className="rounded-xl border border-line overflow-hidden">
      <header className="flex flex-wrap items-center justify-between gap-3 bg-[#fbfaf6] px-4 py-3 border-b border-line">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h3 className="font-display font-bold text-ink text-[15px]">
            {group.deckName ?? (
              <span className="italic text-[#9a9182]">sem deck</span>
            )}
          </h3>
          <span className="text-xs uppercase tracking-wider text-[#8a8170] font-semibold tabular-nums">
            {group.rows.length} lista{group.rows.length === 1 ? "" : "s"}
          </span>
          {group.deckId !== null && (
            <Link
              to={`/theadmin/decks/${group.deckId}/decklists`}
              className="text-xs text-brand-700 font-semibold hover:underline decoration-dotted underline-offset-2"
            >
              ver todas as listas ↗
            </Link>
          )}
        </div>
        {canApplySuggestion && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#6b6356]">
              Sugestão:{" "}
              <span className="font-semibold text-ink">
                {group.suggestedSubarchetypeName}
              </span>
            </span>
            <button
              type="button"
              disabled={anyBusy}
              onClick={() =>
                onClassifyAll(allIds, group.suggestedSubarchetypeId)
              }
              className="rounded bg-brand-700 text-white text-xs font-semibold px-3 py-1.5 hover:bg-brand-800 disabled:opacity-50 transition-colors"
            >
              {anyBusy
                ? "Aplicando..."
                : `Aplicar a ${group.rows.length === 1 ? "esta lista" : "todas"}`}
            </button>
          </div>
        )}
      </header>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white text-brand-900">
            <tr className="text-[11px] uppercase tracking-wide border-b border-[#f3efe6]">
              <th className="text-left px-3 py-2 font-bold">Evento</th>
              <th className="text-left px-3 py-2 font-bold">Data</th>
              <th className="text-left px-3 py-2 font-bold">Jogador</th>
              <th className="text-right px-3 py-2 w-14 font-bold">#</th>
              <th className="text-right px-3 py-2 w-16 font-bold">Pts</th>
              <th className="text-left px-3 py-2 w-72 font-bold">
                Subarquétipo
              </th>
              <th className="text-left px-3 py-2 w-36 font-bold">Lista</th>
            </tr>
          </thead>
          <tbody>
            {group.rows.map((row) => (
              <UnclassifiedRow
                key={row.eventResultId}
                row={row}
                archetypes={archetypes}
                saving={busyIds.has(row.eventResultId)}
                onChange={(subarchetypeId) =>
                  onClassifyOne(row.eventResultId, subarchetypeId)
                }
              />
            ))}
          </tbody>
        </table>
      </div>
    </article>
  );
}

interface RowProps {
  row: AdminUnclassifiedRow;
  archetypes: ArchetypeWithSubarchetypes[];
  saving: boolean;
  onChange: (subarchetypeId: number | null) => void;
}

function UnclassifiedRow({ row, archetypes, saving, onChange }: RowProps) {
  // The <select> stores its own value so the admin can pick something
  // without immediately committing — we only fire the mutation when the
  // value actually changes (which happens on every native change event).
  // The initial value reflects the suggestion so a single accept-click
  // commits it.
  const initial = row.suggestedSubarchetypeId;

  return (
    <tr className="border-t border-[#f3efe6] hover:bg-[#fbfaf6] transition-colors align-top">
      <td className="px-3 py-2.5">
        <Link
          to={`/events/${row.eventId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-ink hover:text-brand-700 hover:underline decoration-dotted underline-offset-2"
        >
          {row.eventName}
        </Link>
      </td>
      <td className="px-3 py-2.5 tabular-nums text-[#6b6356]">
        {row.eventDate}
      </td>
      <td className="px-3 py-2.5 text-[#3f3a31]">{row.playerName}</td>
      <td className="px-3 py-2.5 text-right tabular-nums text-[#6b6356]">
        {row.rank}
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums font-bold text-ink">
        {row.points}
      </td>
      <td className="px-3 py-2.5">
        <SubarchetypeSelect
          archetypes={archetypes}
          value={initial}
          onChange={onChange}
          disabled={saving}
          ariaLabel={`Subarquétipo para ${row.playerName} em ${row.eventName}`}
        />
      </td>
      <td className="px-3 py-2.5">
        {row.hasDecklist ? (
          <Link
            to={`/events/${row.eventId}/decks/${row.eventResultId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-700 font-semibold hover:underline decoration-dotted underline-offset-2 text-xs"
          >
            Abrir lista ↗
          </Link>
        ) : row.meleeDecklistId ? (
          <a
            href={`https://melee.gg/Decklist/View/${row.meleeDecklistId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#6b6356] hover:text-brand-700 hover:underline decoration-dotted underline-offset-2 text-xs"
          >
            melee.gg ↗
          </a>
        ) : (
          <span className="italic text-[#b8b0a0] text-xs">sem snapshot</span>
        )}
      </td>
    </tr>
  );
}
